"use client"

import { useState, useEffect, useRef } from "react"
import { useNavigate } from "react-router-dom"
import "./Header.css"
import logo from "../../assets/images/logo.svg"
import exploreIcon from "../../assets/images/ExploreSiteBTN.png"
import { FaBell, FaUser, FaBars, FaTimes, FaSignOutAlt, FaCrown, FaTachometerAlt, FaSearch } from "react-icons/fa"
import { Menu, Dropdown } from "antd"

// Create a custom event for auth state changes
export const authStateChanged = new Event("authStateChanged")

const Header = ({ openModal }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [user, setUser] = useState(null)
  const [searchQuery, setSearchQuery] = useState("")
  const [showSearchSuggestions, setShowSearchSuggestions] = useState(false)
  const [notificationCount, setNotificationCount] = useState(3)
  const searchRef = useRef(null)
  const navigate = useNavigate()

  // Smart search recommendations based on routes
  const searchRoutes = [
    { path: "/", title: "Home", description: "Main landing page", icon: "🏠", keywords: ["home", "main", "landing"] },
    {
      path: "/about",
      title: "About Us",
      description: "Learn about our company",
      icon: "ℹ️",
      keywords: ["about", "company", "info", "information"],
    },
    {
      path: "/careers",
      title: "Careers",
      description: "Join our team",
      icon: "💼",
      keywords: ["careers", "jobs", "work", "employment", "hiring"],
    },
    {
      path: "/Companies",
      title: "Companies",
      description: "Partner companies",
      icon: "🏢",
      keywords: ["companies", "partners", "business", "corporate"],
    },
    {
      path: "/business",
      title: "Business",
      description: "Business solutions",
      icon: "📊",
      keywords: ["business", "solutions", "enterprise", "corporate"],
    },
    {
      path: "/Contact",
      title: "Contact",
      description: "Get in touch with us",
      icon: "📞",
      keywords: ["contact", "support", "help", "reach", "touch"],
    },
    {
      path: "/Courses/HTML",
      title: "HTML Course",
      description: "Learn HTML programming",
      icon: "🌐",
      keywords: ["html", "web", "programming", "code", "development", "course"],
    },
    {
      path: "/Courses/crypto",
      title: "Crypto Course",
      description: "Learn cryptocurrency",
      icon: "₿",
      keywords: ["crypto", "cryptocurrency", "bitcoin", "blockchain", "course"],
    },
    {
      path: "/dashboard",
      title: "Dashboard",
      description: "Your personal dashboard",
      icon: "📊",
      keywords: ["dashboard", "profile", "account", "personal"],
    },
    {
      path: "/terms-conditions",
      title: "Terms & Conditions",
      description: "Legal terms",
      icon: "📋",
      keywords: ["terms", "conditions", "legal", "agreement"],
    },
    {
      path: "/privacy-policy",
      title: "Privacy Policy",
      description: "Privacy information",
      icon: "🔒",
      keywords: ["privacy", "policy", "data", "protection"],
    },
    {
      path: "/DataProtection",
      title: "Data Protection",
      description: "Data security info",
      icon: "🛡️",
      keywords: ["data", "protection", "security", "privacy"],
    },
  ]

  const updateUserState = () => {
    const token = localStorage.getItem("token")
    const userData = localStorage.getItem("user")

    if (token && userData) {
      try {
        setUser(JSON.parse(userData))
      } catch (error) {
        console.error("Error parsing user data:", error)
        localStorage.removeItem("user")
        localStorage.removeItem("token")
        setUser(null)
      }
    } else {
      setUser(null)
    }
  }

  useEffect(() => {
    updateUserState()
    window.addEventListener("authStateChanged", updateUserState)

    // Click outside to close search suggestions
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowSearchSuggestions(false)
      }
    }

    document.addEventListener("mousedown", handleClickOutside)

    return () => {
      window.removeEventListener("authStateChanged", updateUserState)
      document.removeEventListener("mousedown", handleClickOutside)
    }
  }, [])

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen)
  }

  const handleLogout = () => {
    localStorage.removeItem("token")
    localStorage.removeItem("user")
    localStorage.removeItem("isPremiumUser")
    setUser(null)
    window.dispatchEvent(authStateChanged)
    navigate("/")
  }

  const handleDashboardClick = () => {
    navigate("/dashboard")
  }

  const handleSearchChange = (e) => {
    const value = e.target.value
    setSearchQuery(value)
    setShowSearchSuggestions(value.length > 0)
  }

  const handleSearchSuggestionClick = (path) => {
    navigate(path)
    setSearchQuery("")
    setShowSearchSuggestions(false)
  }

  const getFilteredSuggestions = () => {
    if (!searchQuery) return []

    return searchRoutes
      .filter(
        (route) =>
          route.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
          route.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
          route.keywords.some((keyword) => keyword.toLowerCase().includes(searchQuery.toLowerCase())),
      )
      .slice(0, 5)
  }

  const userMenu = (
    <Menu className="header__user-menu">
      <Menu.Item key="dashboard" onClick={handleDashboardClick} className="header__menu-item">
        <div className="header__menu-item-content">
          <FaTachometerAlt className="header__menu-icon" />
          <span>Dashboard</span>
        </div>
      </Menu.Item>
      <Menu.Divider />
      <Menu.Item key="logout" onClick={handleLogout} className="header__menu-item header__menu-item--logout">
        <div className="header__menu-item-content">
          <FaSignOutAlt className="header__menu-icon" />
          <span>Logout</span>
        </div>
      </Menu.Item>
    </Menu>
  )

  const notifications = (
    <Menu className="header__notifications-menu">
      <div className="header__notifications-header">
        <h4>Announcements</h4>
        {notificationCount > 0 && <span className="header__notifications-count">{notificationCount}</span>}
      </div>

      <Menu.Item key="1" className="header__notification-item">
        <div className="header__notification-content">
          <div className="header__notification-icon header__notification-icon--new">🎉</div>
          <div className="header__notification-text">
            <div className="header__notification-title">New Course Launch!</div>
            <div className="header__notification-desc">Advanced Crypto Trading course is now available</div>
            <div className="header__notification-time">2 hours ago</div>
          </div>
        </div>
      </Menu.Item>

      <Menu.Item key="2" className="header__notification-item">
        <div className="header__notification-content">
          <div className="header__notification-icon header__notification-icon--update">🔔</div>
          <div className="header__notification-text">
            <div className="header__notification-title">Platform Update</div>
            <div className="header__notification-desc">New features added to enhance your learning experience</div>
            <div className="header__notification-time">1 day ago</div>
          </div>
        </div>
      </Menu.Item>

      {user && (
        <Menu.Item key="3" className="header__notification-item">
          <div className="header__notification-content">
            <div className="header__notification-icon header__notification-icon--progress">📈</div>
            <div className="header__notification-text">
              <div className="header__notification-title">Job Portal!</div>
              <div className="header__notification-desc">Coming soon!</div>
              <div className="header__notification-time">3 days ago</div>
            </div>
          </div>
        </Menu.Item>
      )}

      <Menu.Divider />
      <Menu.Item key="view-all" className="header__notification-view-all">
        <div className="header__notification-view-all-content">View All Announcements</div>
      </Menu.Item>
    </Menu>
  )

  const isPremium = localStorage.getItem("isPremiumUser") === "true"
  const filteredSuggestions = getFilteredSuggestions()

  return (
    <header className="header">
      <a href="/" className="header__logo-link">
        <div className="header__logo">
          <img src={logo || "/placeholder.svg"} alt="Inelf Logo" className="header__logo-img" />
          <span className="header__title">inelf</span>
        </div>
      </a>

      <div className="header__search" ref={searchRef}>
        <input
          type="text"
          style={{ marginLeft: "20px" }}
          placeholder="Search courses, pages, or topics..."
          className="header__search-input"
          value={searchQuery}
          onChange={handleSearchChange}
          onFocus={() => searchQuery && setShowSearchSuggestions(true)}
        />
        <div className="header__search-icon">
          <FaSearch />
        </div>

        {showSearchSuggestions && filteredSuggestions.length > 0 && (
          <div className="header__search-suggestions">
            {filteredSuggestions.map((suggestion, index) => (
              <div
                key={index}
                className="header__search-suggestion"
                onClick={() => handleSearchSuggestionClick(suggestion.path)}
              >
                <span className="header__search-suggestion-icon">{suggestion.icon}</span>
                <div className="header__search-suggestion-content">
                  <div className="header__search-suggestion-title">{suggestion.title}</div>
                  <div className="header__search-suggestion-desc">{suggestion.description}</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="header__mobile-toggle" onClick={toggleMobileMenu}>
        {mobileMenuOpen ? <FaTimes /> : <FaBars />}
      </div>

      <div className={`header__right-container ${mobileMenuOpen ? "mobile-open" : ""}`}>
        <Dropdown overlay={notifications} trigger={["click"]} placement="bottomRight">
          <button className="header__notification-btn">
            <FaBell className="header__notification-icon" />
            {notificationCount > 0 && <span className="header__notification-badge">{notificationCount}</span>}
          </button>
        </Dropdown>

        <div className="header__divider2 mobile-hide" />

        <div className="header-right">
          <button className="header__explore-btn">
            <img src={exploreIcon || "/placeholder.svg"} alt="Explore Icon" className="header__explore-icon" />
            <span>Explore Site</span>
          </button>

          {user ? (
            <div className="header__user-profile">
              <Dropdown overlay={userMenu} trigger={["click"]} placement="bottomRight">
                <div className="header__user-info">
                  {isPremium && <FaCrown className="header__premium-icon" />}
                  {user.photoURL ? (
                    <img
                      src={user.photoURL || "/placeholder.svg"}
                      alt={user.displayName || user.email}
                      className="header__user-avatar"
                    />
                  ) : (
                    <div className="header__user-avatar-placeholder">
                      {(user.displayName || user.email || "U").charAt(0).toUpperCase()}
                    </div>
                  )}
                  <span className="header__user-name">{user.displayName || user.email.split("@")[0]}</span>
                </div>
              </Dropdown>
            </div>
          ) : (
            <div className="header__auth-buttons">
              <div className="header__divider mobile-hide" />
              <button className="header__login-btn" onClick={openModal}>
                <FaUser className="header__user-icon" />
                Login
              </button>
              <div className="header__divider mobile-hide" />
              <button className="header__register-btn" onClick={openModal}>
                Register
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  )
}

export default Header
